/**
 * Reading CSS theme tokens back out for the canvas.
 *
 * Every colour in this app is defined once, in `index.css`, as a CSS custom
 * property. DOM elements consume them through Tailwind utilities. A canvas
 * cannot: `ctx.strokeStyle = 'var(--up)'` is silently ignored and you get a
 * black line, so the chart needs literal strings.
 *
 * This module is the only bridge. Call it once per theme change — never in
 * a render loop or a draw callback: `getComputedStyle` forces a style
 * recalculation, and doing that per frame turns a smooth chart into a
 * stuttering one.
 *
 * The tokens it reads are deliberately plain hex in the stylesheet rather
 * than `oklch()`. Canvas2D colour parsing across browsers is not somewhere
 * to be clever, and KLineChart appends alpha suffixes to some of these
 * strings internally, which only works on hex.
 */

export type ThemeName = 'light' | 'dark';
export type ThemeChoice = ThemeName | 'system';

/** localStorage key. Absence means "follow the OS" — see index.html. */
export const THEME_STORAGE_KEY = 'bheema.theme';
export const CVD_STORAGE_KEY = 'bheema.cvd';

/**
 * The palette, as literal colour strings, resolved from the live document.
 *
 * Anything that draws to a canvas or builds an inline `style` takes one of
 * these instead of hardcoding a hex value.
 */
export interface ChartTokens {
  /** Direction — graphical weight (candle bodies, bars, markers). */
  up: string;
  down: string;
  flat: string;
  /** Direction — text weight, higher contrast for numbers. */
  upText: string;
  downText: string;
  upWeak: string;
  downWeak: string;

  surface0: string;
  surface1: string;
  surface2: string;
  surface3: string;
  surfaceInset: string;

  border: string;
  borderStrong: string;

  textHi: string;
  textMid: string;
  textLo: string;

  accent: string;
  accentFg: string;
  accentWeak: string;

  warn: string;
  danger: string;
  info: string;

  grid: string;
  axis: string;
  crosshair: string;

  /** Six-colour, colourblind-safe indicator ramp. */
  series: readonly [string, string, string, string, string, string];
}

const FALLBACK_DARK: ChartTokens = {
  up: '#26a69a',
  down: '#ef5350',
  flat: '#6e7885',
  upText: '#3dd9a4',
  downText: '#ff6b6b',
  upWeak: '#26a69a26',
  downWeak: '#ef535026',
  surface0: '#0e1116',
  surface1: '#151a21',
  surface2: '#1c222b',
  surface3: '#252c37',
  surfaceInset: '#10151b',
  border: '#2a323d',
  borderStrong: '#3a4553',
  textHi: '#e6e9ee',
  textMid: '#a2abb8',
  textLo: '#858f9c',
  accent: '#3b82f6',
  accentFg: '#0b1220',
  accentWeak: '#3b82f626',
  warn: '#f0a93b',
  danger: '#ff5f56',
  info: '#38bdf8',
  grid: '#2a323d',
  axis: '#858f9c',
  crosshair: '#a2abb8',
  series: ['#56b4e9', '#e69f00', '#3dd9a4', '#e58fbf', '#8ab4f8', '#ff9052'],
};

/**
 * Snapshot the current palette from the document.
 *
 * Falls back to the dark palette per-token, so a typo in a variable name
 * produces a slightly wrong colour rather than an invisible chart.
 */
export function readChartTokens(): ChartTokens {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return FALLBACK_DARK;
  }
  const computed = getComputedStyle(document.documentElement);
  const read = (name: string, fallback: string): string => {
    const value = computed.getPropertyValue(name).trim();
    return value.length > 0 ? value : fallback;
  };

  return {
    up: read('--up', FALLBACK_DARK.up),
    down: read('--down', FALLBACK_DARK.down),
    flat: read('--flat', FALLBACK_DARK.flat),
    upText: read('--up-text', FALLBACK_DARK.upText),
    downText: read('--down-text', FALLBACK_DARK.downText),
    upWeak: read('--up-weak', FALLBACK_DARK.upWeak),
    downWeak: read('--down-weak', FALLBACK_DARK.downWeak),

    surface0: read('--surface-0', FALLBACK_DARK.surface0),
    surface1: read('--surface-1', FALLBACK_DARK.surface1),
    surface2: read('--surface-2', FALLBACK_DARK.surface2),
    surface3: read('--surface-3', FALLBACK_DARK.surface3),
    surfaceInset: read('--surface-inset', FALLBACK_DARK.surfaceInset),

    border: read('--border', FALLBACK_DARK.border),
    borderStrong: read('--border-strong', FALLBACK_DARK.borderStrong),

    textHi: read('--text-hi', FALLBACK_DARK.textHi),
    textMid: read('--text-mid', FALLBACK_DARK.textMid),
    textLo: read('--text-lo', FALLBACK_DARK.textLo),

    accent: read('--accent', FALLBACK_DARK.accent),
    accentFg: read('--accent-fg', FALLBACK_DARK.accentFg),
    accentWeak: read('--accent-weak', FALLBACK_DARK.accentWeak),

    warn: read('--warn', FALLBACK_DARK.warn),
    danger: read('--danger', FALLBACK_DARK.danger),
    info: read('--info', FALLBACK_DARK.info),

    grid: read('--chart-grid', FALLBACK_DARK.grid),
    axis: read('--chart-axis', FALLBACK_DARK.axis),
    crosshair: read('--chart-crosshair', FALLBACK_DARK.crosshair),

    series: [
      read('--s1', FALLBACK_DARK.series[0]),
      read('--s2', FALLBACK_DARK.series[1]),
      read('--s3', FALLBACK_DARK.series[2]),
      read('--s4', FALLBACK_DARK.series[3]),
      read('--s5', FALLBACK_DARK.series[4]),
      read('--s6', FALLBACK_DARK.series[5]),
    ],
  };
}

/** One CSS variable, resolved. For one-off inline styles. */
export function readToken(name: string, fallback = ''): string {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name.startsWith('--') ? name : `--${name}`)
    .trim();
  return value.length > 0 ? value : fallback;
}

/** What the OS is currently asking for. */
export function systemTheme(): ThemeName {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/** The theme actually painted right now, read off the document. */
export function currentTheme(): ThemeName {
  if (typeof document === 'undefined') return 'dark';
  return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
}

export { FALLBACK_DARK as FALLBACK_TOKENS };
